import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Download, Phone, Mail, AlertCircle, FileText, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PriceList = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-[#4A5F8F] to-[#2C3E5F] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <FileText className="w-16 h-16 mx-auto mb-6" />
            <h1 className="text-5xl font-bold mb-6">{t('priceList.title')}</h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto mb-8">
              {t('priceList.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Alert className="border-[#4A5F8F] bg-blue-50 mb-8">
            <AlertCircle className="h-5 w-5 text-[#4A5F8F]" />
            <AlertDescription className="text-gray-700 ml-2">
              <strong>{currentLang === 'en' ? 'Important:' : 'ಮುಖ್ಯ:'}</strong> {t('priceList.note')}
            </AlertDescription>
          </Alert>

          {/* Download Section */}
          <Card className="shadow-lg">
            <CardContent className="p-12">
              <div className="text-center">
                <div className="w-24 h-24 bg-[#4A5F8F] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-12 h-12 text-[#4A5F8F]" />
                </div>
                
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {currentLang === 'en' ? 'Comprehensive Price List' : 'ಸಮಗ್ರ ಬೆಲೆ ಪಟ್ಟಿ'}
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  {currentLang === 'en' ? 'All products with complete specifications and pricing information' : 'ಸಂಪೂರ್ಣ ವಿಶೇಷಣಗಳು ಮತ್ತು ಬೆಲೆ ಮಾಹಿತಿಯೊಂದಿಗೆ ಎಲ್ಲಾ ಉತ್ಪನ್ನಗಳು'}
                </p>

                <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-lg mb-8">
                  <Calendar className="w-5 h-5 text-gray-600 mr-2" />
                  <span className="text-gray-700">{t('priceList.lastUpdated')}: December 2025</span>
                </div>

                {/* Download Buttons */}
                <div className="mb-8 space-y-4">
                  <a 
                    href="https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/p752lp3i_Price%20list%20pdf.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      size="lg"
                      className="bg-[#4A5F8F] hover:bg-[#3A4F7F] w-full sm:w-auto"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      {t('priceList.contactButton')}
                    </Button>
                  </a>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <a 
                      href="https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/rutdbfhk_UPVC%20Agriculture%20%20BROUCHER.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        size="lg"
                        variant="outline"
                        className="border-[#4A5F8F] text-[#4A5F8F] hover:bg-[#4A5F8F] hover:text-white w-full"
                      >
                        <FileText className="mr-2 h-5 w-5" />
                        {t('priceList.catalogues.upvcAgriculture')}
                      </Button>
                    </a>
                    
                    <a 
                      href="https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/yw8emm3y_UGD%20BROUCHER.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        size="lg"
                        variant="outline"
                        className="border-[#4A5F8F] text-[#4A5F8F] hover:bg-[#4A5F8F] hover:text-white w-full"
                      >
                        <FileText className="mr-2 h-5 w-5" />
                        {t('priceList.catalogues.ugd')}
                      </Button>
                    </a>
                  </div>
                </div>

                <div className="border-t pt-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {t('priceList.currentPricing.title')}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {t('priceList.currentPricing.description')}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a href="tel:+919036493100">
                      <Button size="lg" className="bg-[#4A5F8F] hover:bg-[#3A4F7F] w-full sm:w-auto">
                        <Phone className="mr-2 h-5 w-5" />
                        +91 9036493100
                      </Button>
                    </a>
                    <a href="mailto:sales@dharanipipes.com">
                      <Button size="lg" className="bg-[#4A5F8F] hover:bg-[#3A4F7F] w-full sm:w-auto">
                        <Mail className="mr-2 h-5 w-5" />
                        sales@dharanipipes.com
                      </Button>
                    </a>
                    <Link to="/enquiry">
                      <Button size="lg" variant="outline" className="border-[#4A5F8F] text-[#4A5F8F] hover:bg-[#4A5F8F] hover:text-white w-full sm:w-auto">
                        <Mail className="mr-2 h-5 w-5" />
                        {t('enquiry.form.submit')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Product Categories */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t('priceList.productRange')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: currentLang === 'en' ? 'UPVC Agriculture Pipes' : t('priceList.products.upvcAgriculture'), standard: 'IS 4985', range: '20mm - 250mm' },
                { name: currentLang === 'en' ? 'SWR Pipes' : t('priceList.products.swr'), standard: 'IS 13592', range: '40mm - 200mm' },
                { name: currentLang === 'en' ? 'Electric Conduits' : t('priceList.products.conduits'), standard: 'IS 9535', range: '16mm - 150mm' },
                { name: currentLang === 'en' ? 'UGD Pipes' : t('priceList.products.ugd'), standard: 'IS 15328', range: '63mm - 250mm' },
                { name: currentLang === 'en' ? 'UPVC Plumbing Pipes' : t('priceList.products.plumbing'), standard: 'ASTM 1785', range: 'Sch 40/80/120' },
                { name: currentLang === 'en' ? 'Casing Pipes' : t('priceList.products.casing'), standard: 'IS 12818', range: '100mm - 300mm' },
              ].map((product, index) => (
                <Card key={index} className="border-2 border-gray-100 hover:border-[#4A5F8F] transition-colors duration-300">
                  <CardContent className="p-6">
                    <Badge className="mb-3 bg-[#4A5F8F]">{product.standard}</Badge>
                    <h4 className="font-semibold text-gray-900 mb-2">{product.name}</h4>
                    <p className="text-sm text-gray-600">{product.range}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Discount Information */}
          <Card className="mt-12 bg-gradient-to-br from-[#4A5F8F] to-[#2C3E5F] text-white border-none">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4 text-center">
                {currentLang === 'en' ? 'Volume-Based Discounts Available' : 'ಪರಿಮಾಣ-ಆಧಾರಿತ ರಿಯಾಯಿತಿಗಳು ಲಭ್ಯವಿದೆ'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div className="text-center p-4 bg-white bg-opacity-10 rounded-lg">
                  <div className="text-3xl font-bold mb-2">
                    {currentLang === 'en' ? 'Standard' : 'ಪ್ರಮಾಣಿತ'}
                  </div>
                  <p className="text-sm text-gray-200">
                    {currentLang === 'en' ? 'Regular quantity orders' : 'ನಿಯಮಿತ ಪ್ರಮಾಣದ ಆದೇಶಗಳು'}
                  </p>
                </div>
                <div className="text-center p-4 bg-white bg-opacity-20 rounded-lg border-2 border-white">
                  <div className="text-3xl font-bold mb-2">
                    {currentLang === 'en' ? 'Bulk' : 'ಬೃಹತ್'}
                  </div>
                  <p className="text-sm text-gray-200">
                    {currentLang === 'en' ? 'Special discounts for large orders' : 'ದೊಡ್ಡ ಆದೇಶಗಳಿಗೆ ವಿಶೇಷ ರಿಯಾಯಿತಿಗಳು'}
                  </p>
                </div>
                <div className="text-center p-4 bg-white bg-opacity-10 rounded-lg">
                  <div className="text-3xl font-bold mb-2">
                    {currentLang === 'en' ? 'Project' : 'ಯೋಜನೆ'}
                  </div>
                  <p className="text-sm text-gray-200">
                    {currentLang === 'en' ? 'Customized pricing for contractors' : 'ಗುತ್ತಿಗೆದಾರರಿಗೆ ಕಸ್ಟಮೈಸ್ಡ್ ಬೆಲೆ'}
                  </p>
                </div>
              </div>
              <p className="text-center mt-6 text-gray-200">
                {currentLang === 'en' ? 'Contact us for detailed discount structures and project-specific pricing' : 'ವಿವರವಾದ ರಿಯಾಯಿತಿ ರಚನೆಗಳು ಮತ್ತು ಯೋಜನೆ-ನಿರ್ದಿಷ್ಟ ಬೆಲೆಗಾಗಿ ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ'}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default PriceList;
